import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Badge, Table, Modal, Form, Alert } from "react-bootstrap";
import { Users, Clock, CreditCard, Plus, Calendar, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";
import {
  whoami,
  getSchoolAccess,
  getTrialInstancesBySchool,
  getPurchaseInstancesBySchool,
  getActiveTrialDefinitions,
  getActivePurchaseOptions,
  createTrialInstanceBySchoolAndTrial,
  createPurchaseByRollnumber,
  applyTrialExtension,
  markTrialExpired,
  markTrialCompleted,
  markPurchaseActive,
  markPurchaseExpired,
  markPurchaseCancelled,
  getCurrentAcademicYear,
} from "../services/APIClient";
import "../components/SchoolAccessManager.css";

function SchoolAccessManager() {
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("Admin");
  const [isPortrait, setIsPortrait] = useState(window.matchMedia("(orientation: portrait)").matches);

  // School State
  const [schoolid, setSchoolid] = useState("");
  const [schoolName, setSchoolName] = useState("Please enter school identifier and press 'Find School'");
  const [rollnumber, setRollnumber] = useState("");
  const isSchoolFound = schoolid && schoolid !== "";

  // Access Data
  const [accessData, setAccessData] = useState(null);
  const [trialHistory, setTrialHistory] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [showGrantTrialModal, setShowGrantTrialModal] = useState(false);
  const [showRecordPurchaseModal, setShowRecordPurchaseModal] = useState(false);

  // Options
  const [trialDefinitions, setTrialDefinitions] = useState([]);
  const [purchaseOptions, setPurchaseOptions] = useState([]);

  // Lookup maps for displaying names
  const [trialDefinitionsMap, setTrialDefinitionsMap] = useState({});
  const [purchaseOptionsMap, setPurchaseOptionsMap] = useState({});

  // Messages
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
    });

    loadTrialDefinitions();
    loadPurchaseOptions();
  }, []);

  useEffect(() => {
    if (isSchoolFound) {
      loadSchoolAccessData();
    }
  }, [schoolid]);

  const loadTrialDefinitions = async () => {
    try {
      const data = await getActiveTrialDefinitions();
      setTrialDefinitions(data);
      
      // Create lookup map: id -> name
      const map = {};
      data.forEach(trial => {
        map[trial.id] = trial.name;
      });
      setTrialDefinitionsMap(map);
    } catch (err) {
      console.error("Error loading trial definitions:", err);
    }
  };

  const loadPurchaseOptions = async () => {
    try {
      const data = await getActivePurchaseOptions();
      setPurchaseOptions(data);
      
      // Create lookup map: id -> name
      const map = {};
      data.forEach(option => {
        map[option.id] = option.name;
      });
      setPurchaseOptionsMap(map);
    } catch (err) {
      console.error("Error loading purchase options:", err);
    }
  };

  const loadSchoolAccessData = async () => {
    setLoading(true);
    try {
      const [access, trials, purchases] = await Promise.all([
        getSchoolAccess(schoolid),
        getTrialInstancesBySchool(schoolid),
        getPurchaseInstancesBySchool(schoolid),
      ]);
      
      setAccessData(access);
      // Ensure we always set arrays, even if API returns null/undefined/object
      setTrialHistory(Array.isArray(trials) ? trials : []);
      setPurchaseHistory(Array.isArray(purchases) ? purchases : []);
    } catch (err) {
      setError("Failed to load school access data");
      console.error(err);
      // Set empty arrays on error
      setTrialHistory([]);
      setPurchaseHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantTrial = () => {
    setShowGrantTrialModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleRecordPurchase = () => {
    setShowRecordPurchaseModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleExtendTrial = async (trialId) => {
    try {
      await applyTrialExtension(schoolid);
      setSuccess("Trial extended by 7 days!");
      loadSchoolAccessData();
    } catch (err) {
      setError("Failed to extend trial");
      console.error(err);
    }
  };

  const handleExpireTrial = async (trialId) => {
    if (!window.confirm("Are you sure you want to expire this trial?")) return;
    
    try {
      await markTrialExpired(schoolid, true);
      setSuccess("Trial marked as expired");
      loadSchoolAccessData();
    } catch (err) {
      setError("Failed to expire trial");
      console.error(err);
    }
  };

  const handleActivatePurchase = async (purchaseId) => {
    try {
      await markPurchaseActive(purchaseId);
      setSuccess("Purchase activated!");
      loadSchoolAccessData();
    } catch (err) {
      setError("Failed to activate purchase");
      console.error(err);
    }
  };

  const handleExpirePurchase = async (purchaseId) => {
    if (!window.confirm("Are you sure you want to expire this purchase?")) return;
    
    try {
      await markPurchaseExpired(purchaseId);
      setSuccess("Purchase marked as expired");
      loadSchoolAccessData();
    } catch (err) {
      setError("Failed to expire purchase");
      console.error(err);
    }
  };

  return (
    <div>
      <Container fluid className="school-access-container">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} teacherid={teacherid} />

        <Row className="school-access-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="school-access-content">
            <div className="school-access-header">
              <h2 className="school-access-page-title">
                <Users className="me-2" size={28} />
                School Access Manager
              </h2>
              <p className="school-access-page-subtitle">
                Manage trials and purchases for schools
              </p>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            <div className="school-selector-section mb-4">
              <SchoolRollNumberInput
                onSchoolIdRetrieved={(id) => {
                  setSchoolid(id);
                }}
                schoolNameRetrieved={(name, rollNum) => {
                  setSchoolName(name);
                  if (rollNum) setRollnumber(rollNum);
                }}
              />
            </div>

            {isSchoolFound && (
              <>
                <div className="selected-school-banner mb-4">
                  <h3 className="school-name">{schoolName}</h3>
                  {rollnumber && (
                    <p className="text-muted mb-0">Roll Number: {rollnumber}</p>
                  )}
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <CurrentAccessStatus 
                      accessData={accessData}
                      trialHistory={trialHistory}
                      purchaseHistory={purchaseHistory}
                      trialDefinitionsMap={trialDefinitionsMap}
                      purchaseOptionsMap={purchaseOptionsMap}
                    />

                    <Row className="mb-4">
                      <Col md={6}>
                        <Card className="action-card action-card-trial" onClick={handleGrantTrial}>
                          <Card.Body>
                            <div className="action-card-icon">
                              <Clock size={24} />
                            </div>
                            <h5 className="action-card-title">Grant Trial</h5>
                            <p className="action-card-description">
                              Assign a trial period to this school
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6}>
                        <Card className="action-card action-card-purchase" onClick={handleRecordPurchase}>
                          <Card.Body>
                            <div className="action-card-icon">
                              <CreditCard size={24} />
                            </div>
                            <h5 className="action-card-title">Record Purchase</h5>
                            <p className="action-card-description">
                              Create a purchase record when school pays
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    <TrialHistorySection
                      trials={trialHistory}
                      trialDefinitionsMap={trialDefinitionsMap}
                      onExtend={handleExtendTrial}
                      onExpire={handleExpireTrial}
                    />

                    <PurchaseHistorySection
                      purchases={purchaseHistory}
                      purchaseOptionsMap={purchaseOptionsMap}
                      onActivate={handleActivatePurchase}
                      onExpire={handleExpirePurchase}
                    />
                  </>
                )}
              </>
            )}

            {!isSchoolFound && (
              <div className="no-school-message">
                <Users size={64} className="text-muted mb-3" />
                <p className="text-muted">Please select a school to manage access</p>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}

      <GrantTrialModal
        show={showGrantTrialModal}
        onHide={() => setShowGrantTrialModal(false)}
        schoolid={schoolid}
        trialDefinitions={trialDefinitions}
        onSuccess={(msg) => {
          setSuccess(msg);
          loadSchoolAccessData();
          setShowGrantTrialModal(false);
        }}
        onError={(msg) => setError(msg)}
      />

      <RecordPurchaseModal
        show={showRecordPurchaseModal}
        onHide={() => setShowRecordPurchaseModal(false)}
        rollnumber={rollnumber}
        purchaseOptions={purchaseOptions}
        onSuccess={(msg) => {
          setSuccess(msg);
          loadSchoolAccessData();
          setShowRecordPurchaseModal(false);
        }}
        onError={(msg) => setError(msg)}
      />
    </div>
  );
}

// Current Access Status Component
function CurrentAccessStatus({ accessData, trialHistory, purchaseHistory, trialDefinitionsMap, purchaseOptionsMap }) {
  if (!accessData) return null;

  // Date formatter for Irish format (dd/MM/yyyy)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Find active trial - check both completed flag and end date
  const activeTrial = Array.isArray(trialHistory) 
    ? trialHistory.find(t => {
        const endDate = new Date(t.end_date);
        const now = new Date();
        // NOT active if: expired, completed, or converted to purchase
        return !t.expired && !t.completed && !t.converted_to_purchase && endDate >= now;
      })
    : null;

  // Find active purchase - case insensitive status check
  const activePurchase = Array.isArray(purchaseHistory)
    ? purchaseHistory.find(p => {
        const statusLower = (p.status || '').toLowerCase();
        return statusLower === 'active' || (!p.expired && !p.cancelled);
      })
    : null;

  const hasActiveTrial = !!activeTrial;
  const hasActivePurchase = !!activePurchase;

  console.log('Access Status Debug:', {
    hasActiveTrial,
    hasActivePurchase,
    activeTrial,
    activePurchase,
    trialHistory,
    purchaseHistory
  });

  return (
    <Card className="access-status-card mb-4">
      <Card.Body>
        <h5 className="mb-3">Current Access Status</h5>
        <Row>
          <Col md={6}>
            <div className="status-item">
              <div className="d-flex align-items-center mb-2">
                <Clock size={20} className="me-2" />
                <strong>Trial Status</strong>
              </div>
              {hasActiveTrial ? (
                <>
                  <Badge bg="warning" className="status-badge mb-2">
                    🟡 Active Trial
                  </Badge>
                  <div className="mt-2">
                    <div className="text-muted small">
                      <strong>Type:</strong> {trialDefinitionsMap[activeTrial.trial_definition_id] || "Unknown"}
                    </div>
                    <div className="text-muted small">
                      <strong>Expires:</strong> {formatDate(activeTrial.end_date)}
                    </div>
                  </div>
                </>
              ) : (
                <Badge bg="secondary" className="status-badge">
                  No Active Trial
                </Badge>
              )}
            </div>
          </Col>
          <Col md={6}>
            <div className="status-item">
              <div className="d-flex align-items-center mb-2">
                <CreditCard size={20} className="me-2" />
                <strong>Purchase Status</strong>
              </div>
              {hasActivePurchase ? (
                <>
                  <Badge bg="success" className="status-badge mb-2">
                    🟢 Active Purchase
                  </Badge>
                  <div className="mt-2">
                    <div className="text-muted small">
                      <strong>Package:</strong> {purchaseOptionsMap[activePurchase.purchase_option_id] || "Unknown"}
                    </div>
                    <div className="text-muted small">
                      <strong>Academic Year:</strong> {activePurchase.academicyear || activePurchase.academic_year || "N/A"}
                    </div>
                    <div className="text-muted small">
                      <strong>Purchaser:</strong> {activePurchase.purchaser_name || "N/A"}
                    </div>
                  </div>
                </>
              ) : (
                <Badge bg="secondary" className="status-badge">
                  No Active Purchase
                </Badge>
              )}
            </div>
          </Col>
        </Row>
        {accessData.grade_levels && accessData.grade_levels.length > 0 && (
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
            <small className="text-muted">
              <strong>Available Grades:</strong> {accessData.grade_levels.join(", ")}
            </small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

// Trial History Section
function TrialHistorySection({ trials, trialDefinitionsMap, onExtend, onExpire }) {
  // Safety check - ensure trials is always an array
  const trialsList = Array.isArray(trials) ? trials : [];
  
  // Date formatter for Irish format (dd/MM/yyyy)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  if (trialsList.length === 0) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center py-4">
          <Clock size={48} className="text-muted mb-2" />
          <p className="text-muted mb-0">No trial history</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Body>
        <h5 className="mb-3">
          <Clock size={20} className="me-2" />
          Trial History
        </h5>
        <Table responsive hover>
          <thead>
            <tr>
              <th>Trial Type</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {trialsList.map((trial) => (
              <tr key={trial.id}>
                <td>
                  {trialDefinitionsMap[trial.trial_definition_id] || trial.trial_definition_id}
                </td>
                <td>{formatDate(trial.start_date)}</td>
                <td>{formatDate(trial.end_date)}</td>
                <td>
                  {trial.expired ? (
                    <Badge bg="danger">Expired</Badge>
                  ) : trial.completed ? (
                    <Badge bg="info">Completed</Badge>
                  ) : (
                    <Badge bg="success">Active</Badge>
                  )}
                </td>
                <td>
                  {!trial.expired && !trial.completed && (
                    <>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onExtend(trial.id)}
                        className="me-2"
                      >
                        <Plus size={14} className="me-1" />
                        Extend
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => onExpire(trial.id)}
                      >
                        <XCircle size={14} className="me-1" />
                        Expire
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

// Purchase History Section
function PurchaseHistorySection({ purchases, purchaseOptionsMap, onActivate, onExpire }) {
  // Safety check - ensure purchases is always an array
  const purchasesList = Array.isArray(purchases) ? purchases : [];
  
  // Date formatter for Irish format (dd/MM/yyyy)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  if (purchasesList.length === 0) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center py-4">
          <CreditCard size={48} className="text-muted mb-2" />
          <p className="text-muted mb-0">No purchase history</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Body>
        <h5 className="mb-3">
          <CreditCard size={20} className="me-2" />
          Purchase History
        </h5>
        <Table responsive hover>
          <thead>
            <tr>
              <th>Package</th>
              <th>Academic Year</th>
              <th>Purchaser</th>
              <th>Payment Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {purchasesList.map((purchase) => (
              <tr key={purchase.id}>
                <td>
                  {purchaseOptionsMap[purchase.purchase_option_id] || purchase.purchase_option_id}
                </td>
                <td>{purchase.academicyear || purchase.academic_year || "N/A"}</td>
                <td>{purchase.purchaser_name || "N/A"}</td>
                <td>{formatDate(purchase.payment_date)}</td>
                <td>
                  {purchase.status === 'expired' || purchase.expired ? (
                    <Badge bg="danger">Expired</Badge>
                  ) : purchase.status === 'cancelled' || purchase.cancelled ? (
                    <Badge bg="secondary">Cancelled</Badge>
                  ) : (
                    <Badge bg="success">Active</Badge>
                  )}
                </td>
                <td>
                  {purchase.status === 'expired' || purchase.expired || purchase.status === 'cancelled' || purchase.cancelled ? (
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => onActivate(purchase.id)}
                    >
                      <CheckCircle size={14} className="me-1" />
                      Activate
                    </Button>
                  ) : (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onExpire(purchase.id)}
                    >
                      <XCircle size={14} className="me-1" />
                      Expire
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

// Grant Trial Modal
function GrantTrialModal({ show, onHide, schoolid, trialDefinitions, onSuccess, onError }) {
  const [selectedTrial, setSelectedTrial] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await createTrialInstanceBySchoolAndTrial(schoolid, selectedTrial);
      onSuccess("Trial granted successfully!");
      setSelectedTrial("");
    } catch (err) {
      console.error("Error granting trial:", err);
      onError(err.message || "Failed to grant trial");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Grant Trial</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Trial Type *</Form.Label>
            <Form.Select
              value={selectedTrial}
              onChange={(e) => setSelectedTrial(e.target.value)}
              required
            >
              <option value="">Choose a trial...</option>
              {trialDefinitions.map((trial) => (
                <option key={trial.id} value={trial.id}>
                  {trial.name} ({trial.duration} days)
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Granting..." : "Grant Trial"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// Record Purchase Modal
function RecordPurchaseModal({ show, onHide, rollnumber, purchaseOptions, onSuccess, onError }) {
  const [formData, setFormData] = useState({
    purchase_option_id: "",
    purchaser_name: "",
    academicyear: getCurrentAcademicYear(),
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData({
        purchase_option_id: "",
        purchaser_name: "",
        academicyear: getCurrentAcademicYear(),
      });
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await createPurchaseByRollnumber(rollnumber, formData);
      onSuccess("Purchase recorded successfully!");
    } catch (err) {
      console.error("Error recording purchase:", err);
      onError(err.message || "Failed to record purchase");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Record Purchase</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info">
            <strong>School:</strong> {rollnumber}
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Purchase Package *</Form.Label>
            <Form.Select
              value={formData.purchase_option_id}
              onChange={(e) => setFormData({ ...formData, purchase_option_id: e.target.value })}
              required
            >
              <option value="">Choose a package...</option>
              {purchaseOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name} - {option.currency} {option.pricing_tiers?.[0]?.price_per_student || 'N/A'}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Purchaser Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., John Smith"
              value={formData.purchaser_name}
              onChange={(e) => setFormData({ ...formData, purchaser_name: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Academic Year *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., 2025-26"
              value={formData.academicyear}
              onChange={(e) => setFormData({ ...formData, academicyear: e.target.value })}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={submitting}>
            {submitting ? "Recording..." : "Record Purchase"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default SchoolAccessManager;